import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { fetchAdminBusinesses, updateBusinessActiveState, type AdminBusinessRow } from "@/lib/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export default function AdminBusinesses() {
  const [businesses, setBusinesses] = useState<AdminBusinessRow[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const rows = await fetchAdminBusinesses();
      setBusinesses(rows);
      setError("");
    } catch (nextError: any) {
      setError(nextError.message ?? "Unable to load businesses.");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = businesses.filter((business) =>
    [business.name, business.email, business.ownerEmail].some((value) => value.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-950">Businesses</h1>
          <p className="mt-2 text-sm text-slate-600">Review all tenants, their owners, current billing state, and activation status.</p>
        </div>
        <div className="w-full max-w-sm">
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search business, owner, or email" />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <p>{error}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Business</th>
                <th className="px-5 py-3 font-medium">Owner</th>
                <th className="px-5 py-3 font-medium">Subscription</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {filtered.map((business) => (
                <tr key={business.id}>
                  <td className="px-5 py-4">
                    <div>
                      <p className="font-medium text-slate-950">{business.name}</p>
                      <p className="text-slate-500">{business.email || "No email set"}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{business.ownerEmail}</td>
                  <td className="px-5 py-4">
                    <div>
                      <p className="font-medium text-slate-950">{business.subscriptionPlan}</p>
                      <p className="text-slate-500">{money.format((business.subscriptionAmountCents ?? 0) / 100)} • {business.subscriptionStatus}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${business.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                      {business.isActive ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-500">{new Date(business.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await updateBusinessActiveState(business.id, !business.isActive);
                        await load();
                      }}
                    >
                      {business.isActive ? "Deactivate" : "Reactivate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
